import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import type { RouteContext } from '@atlassian/react-resource-router';
import { LegacyPage } from '@atlassian/jira-router-routes-common/src/ui/legacy-page/main.tsx';
import CreateOAuth2Modal from '@atlassian/jira-applinks-administration/src/ui/hybrid/create-oauth2-modal/index.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';

/**
 * Add `initiateApplinksOAuth2Creation`, `refreshApplinksInitiatedFromReact`, and
 * `AppLinks.Creation.Dialogs.doOAuth2FinalizationInitiatedFromReact` to the global window object.
 *
 * Is this gross? Yes.
 * Is it fine? Also yes, because the legacy applinks frontend extensively stores things in `window`.
 *
 * Read on for an explanation of how the two systems interact.
 *
 * ## Legacy -> Modern:
 * In the legacy frontend, we invoke `window.initiateApplinksOAuth2Creation(url)` from the old
 * dialogue to delegate to the new React based dialogue.
 *
 *
 * ## Modern -> Legacy
 * Once we have created the Applink, we can call `doOAuth2FinalizationInitiatedFromReact` to reset the creation
 * state of the original page. This doesn't do a whole lot - it resets the button and clears the state.
 *
 * Following this, we can optionally invoke `refreshApplinksInitiatedFromReact` to update the legacy list view. This
 * prevents awkward full-page refreshes when using the modal.
 *
 */
declare global {
	interface Window {
		initiateApplinksOAuth2Creation?: (targetUrl: string) => void;
		refreshApplinksInitiatedFromReact?: () => void;
		AppLinks: {
			Creation: {
				Process: {
					doOAuth2FinalizationInitiatedFromReact?: () => void;
				};
			};
		};
	}
}

type Props = RouteContext;

const PARENT_ELEMENT_SELECTOR = '.applinks-tunnel-header';
const PORTAL_ELEMENT_KEY = 'react-applinks-button';

export const LOG_LOCATION = 'applinks.administration.legacy';

const refreshLegacyPageState = () => {
	globalThis.window.refreshApplinksInitiatedFromReact?.();
	globalThis.window.AppLinks.Creation.Process.doOAuth2FinalizationInitiatedFromReact?.();
};

/**
 * React portal containing the modal. We use a synchronous modal because the
 * EntryPoint API is not designed for situations with no trigger element.
 */
const PortalledElement = () => {
	const [modalContext, setModalContext] = useState<string | null>(null);

	const openOAuth2Modal = useCallback((targetUrl: string) => {
		setModalContext(targetUrl);
	}, []);
	const closeOAuth2Modal = useCallback(() => {
		setModalContext(null);
	}, []);

	useEffect(() => {
		globalThis.window.initiateApplinksOAuth2Creation = openOAuth2Modal;
	}, [openOAuth2Modal]);

	return (
		<>
			<ModalTransition>
				{modalContext && (
					<Modal
						onClose={closeOAuth2Modal}
						onCloseComplete={refreshLegacyPageState}
						shouldScrollInViewport
					>
						<CreateOAuth2Modal onClose={closeOAuth2Modal} targetUrl={modalContext} />
					</Modal>
				)}
			</ModalTransition>
		</>
	);
};

/**
 * Creates a portal in the Applinks header (i.e. where the "Create link" button is)
 */
const Portal = (_: {}) => {
	const linkElement = globalThis?.document.querySelector<HTMLElement>(PARENT_ELEMENT_SELECTOR);
	if (linkElement) {
		return createPortal(<PortalledElement />, linkElement, PORTAL_ELEMENT_KEY);
	}

	log.safeErrorWithoutCustomerData(
		LOG_LOCATION,
		`No link element found in the DOM (with selector \`${PARENT_ELEMENT_SELECTOR}\`)`,
		undefined,
	);
	return null;
};

/**
 * Inject React into the Legacy frontend
 */
const ModernApplinksCreateModal = (props: Props) => {
	return (
		<>
			<LegacyPage {...props} />
			<Portal />
		</>
	);
};

export default ModernApplinksCreateModal;
